import type { ChapterRef } from "../data-model/novel.js";
import type { ChapterIndex } from "../data-model/primitives.js";
import type { EventBus } from "../core/events.js";
import { CancelledError } from "../core/errors.js";

export const TOC_PROGRESS_EMIT_EVERY = 50;

export interface WalkTocRange {
  readonly from?: ChapterIndex;
  readonly to?: ChapterIndex;
}

export interface WalkTocOptions {
  readonly range?: WalkTocRange;
  readonly bus: EventBus;
  readonly signal: AbortSignal;
  readonly onRef: (ref: ChapterRef) => Promise<void> | void;
}

/**
 * Walks a ChapterRef source, applying the chapter-range window and progress
 * throttle once. The source MUST yield refs in ascending `index` order — once a
 * ref past `range.to` is seen, iteration short-circuits (no later ref can be in
 * range). Returns the count of in-range refs forwarded to `onRef`.
 */
export async function walkToc(
  source: AsyncIterable<ChapterRef>,
  opts: WalkTocOptions,
): Promise<number> {
  const from = opts.range?.from;
  const to = opts.range?.to;
  let count = 0;
  if (opts.signal.aborted) throw new CancelledError({ cause: opts.signal.reason });
  for await (const ref of source) {
    if (opts.signal.aborted) throw new CancelledError({ cause: opts.signal.reason });
    if (to !== undefined && ref.index > to) break;
    if (from !== undefined && ref.index < from) continue;
    await opts.onRef(ref);
    count++;
    if (count % TOC_PROGRESS_EMIT_EVERY === 0) opts.bus.emit({ type: "toc:progress", discovered: count });
  }
  opts.bus.emit({ type: "toc:progress", discovered: count });
  opts.bus.emit({ type: "toc:complete", total: count });
  return count;
}
